import { ChangeEvent, KeyboardEvent, useState } from 'react'

import { IconButton, TextField } from '@joor/design-system'
import styled from 'styled-components/macro'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  input {
    width: 246px;
  }
`

type Props = {
  onSubmitNewMessage: (message: string) => void
  disable?: boolean
}

/**
 * True while an input method editor still has an open composition. `isComposing`
 * is the standard flag; keyCode 229 is the fallback browsers use to report a key
 * the editor swallowed. Both are only meaningful on keydown - React zeroes
 * `keyCode` on synthetic keypress events.
 */
const isComposing = (event: KeyboardEvent<HTMLInputElement>): boolean =>
  event.nativeEvent.isComposing || event.keyCode === 229

export const MessagesForm = ({
  onSubmitNewMessage,
  disable,
}: Props): JSX.Element => {
  const [text, setText] = useState('')
  const { trackEvent } = useUserAnalytics()

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault()
    setText(event.target.value)
  }

  const handleSubmit = (): void => {
    onSubmitNewMessage(text.trim())
    setText('')
    trackEvent('Order Review: Messages Modal (Send)')
  }

  // Handled on keydown rather than keypress: keydown fires for the enter key an
  // input method editor consumes, and carries the flags needed to recognise it.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // Enter is also how an input method editor (Japanese, Chinese, Korean)
    // accepts a conversion candidate. Submitting on that keystroke sends the
    // text as it stood before the candidate was accepted, silently dropping
    // the characters the writer just chose. Wait for a second, non-composing
    // enter key instead. sc-259268
    if (isComposing(event)) {
      return
    }
    if (event.code === 'Space' && text.length === 0) {
      event.preventDefault()
      return
    }
    if (event.code === 'Enter' && text.length > 0) {
      handleSubmit()
    }
  }

  return (
    <Container>
      <TextField
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={'Send a comment'}
        disabled={disable}
      />
      <IconButton
        iconName="plane"
        onClick={handleSubmit}
        disabled={!text.length || disable}
      />
    </Container>
  )
}
